/**
 * pH Sort - substance bank.
 *
 * Each substance has a representative pH (typical aqueous value at room
 * temperature) and a category band:
 *   strong-acid   (0–2)
 *   weak-acid     (3–6)
 *   neutral       (7)
 *   weak-base     (8–10)
 *   strong-base   (11–14)
 *
 * The "why" string is the pedagogical disclosure for the result banner
 * and end-of-match recap, mirroring the Glassware ID pattern.
 */

export type PhBand =
  | "strong-acid"
  | "weak-acid"
  | "neutral"
  | "weak-base"
  | "strong-base";

export interface PhSubstance {
  id: string;
  name: string;
  /** Typical aqueous pH used to determine the band. */
  ph: number;
  band: PhBand;
  why: string;
}

export const PH_BAND_LABEL: Record<PhBand, string> = {
  "strong-acid": "Strong acid (0–2)",
  "weak-acid": "Weak acid (3–6)",
  neutral: "Neutral (7)",
  "weak-base": "Weak base (8–10)",
  "strong-base": "Strong base (11–14)",
};

export const PH_BAND_SHORT: Record<PhBand, string> = {
  "strong-acid": "Strong acid",
  "weak-acid": "Weak acid",
  neutral: "Neutral",
  "weak-base": "Weak base",
  "strong-base": "Strong base",
};

/** Ordered low → high for adjacent-band partial credit. */
export const PH_BAND_ORDER: PhBand[] = [
  "strong-acid",
  "weak-acid",
  "neutral",
  "weak-base",
  "strong-base",
];

export const phSubstances: PhSubstance[] = [
  // Strong acids
  {
    id: "battery-acid",
    name: "Battery acid (H₂SO₄)",
    ph: 0.5,
    band: "strong-acid",
    why: "Concentrated sulfuric acid fully dissociates in water, releasing a high concentration of H⁺ ions - pH < 1.",
  },
  {
    id: "hcl-stomach",
    name: "Stomach acid (HCl)",
    ph: 1.5,
    band: "strong-acid",
    why: "Gastric HCl is a strong acid that fully ionises in water; physiological pH sits between 1 and 2 to digest proteins.",
  },
  {
    id: "hno3",
    name: "Nitric acid (HNO₃)",
    ph: 1,
    band: "strong-acid",
    why: "HNO₃ is one of the six common strong acids; it dissociates completely, giving very low pH.",
  },
  {
    id: "lemon-juice",
    name: "Lemon juice",
    ph: 2,
    band: "strong-acid",
    why: "Citric acid is technically weak, but lemon juice is concentrated enough that its pH falls in the 2–3 range - borderline strong-acid territory.",
  },
  // Weak acids
  {
    id: "vinegar",
    name: "Vinegar (acetic acid)",
    ph: 3,
    band: "weak-acid",
    why: "Acetic acid only partially dissociates (Ka ≈ 1.8 × 10⁻⁵), so household vinegar (~5%) sits around pH 2.5–3.5.",
  },
  {
    id: "orange-juice",
    name: "Orange juice",
    ph: 3.5,
    band: "weak-acid",
    why: "Citric and ascorbic acids in citrus partially ionise - typical pH 3–4.",
  },
  {
    id: "tomato",
    name: "Tomato juice",
    ph: 4.3,
    band: "weak-acid",
    why: "Mostly malic and citric acids - partially ionised, giving a mildly acidic pH around 4.",
  },
  {
    id: "black-coffee",
    name: "Black coffee",
    ph: 5,
    band: "weak-acid",
    why: "Chlorogenic and quinic acids put brewed coffee around pH 5 - clearly acidic but not aggressive.",
  },
  {
    id: "rain",
    name: "Rainwater (clean)",
    ph: 5.6,
    band: "weak-acid",
    why: "Atmospheric CO₂ dissolves into rain forming carbonic acid, so even unpolluted rain is mildly acidic (~5.6).",
  },
  {
    id: "milk",
    name: "Milk",
    ph: 6.5,
    band: "weak-acid",
    why: "Lactic acid and dissolved CO₂ make fresh milk slightly acidic - pH 6.4–6.8.",
  },
  // Neutral
  {
    id: "pure-water",
    name: "Pure water",
    ph: 7,
    band: "neutral",
    why: "At 25 °C, [H⁺] = [OH⁻] = 10⁻⁷ mol/L by definition - pH exactly 7.",
  },
  {
    id: "blood",
    name: "Human blood",
    ph: 7.4,
    band: "neutral",
    why: "Tightly buffered by the bicarbonate system to pH 7.35–7.45 - close enough to count as neutral on the scale.",
  },
  {
    id: "saliva",
    name: "Saliva",
    ph: 6.8,
    band: "neutral",
    why: "Resting saliva pH is 6.7–7.4 - close to neutral; bicarbonate buffering protects enamel from acids.",
  },
  {
    id: "tears",
    name: "Tears",
    ph: 7.2,
    band: "neutral",
    why: "Tear fluid is buffered around pH 7.0–7.4 - essentially neutral.",
  },
  // Weak bases
  {
    id: "seawater",
    name: "Seawater",
    ph: 8.1,
    band: "weak-base",
    why: "Dissolved carbonates and bicarbonates buffer the ocean to a mildly alkaline pH around 8.",
  },
  {
    id: "baking-soda",
    name: "Baking soda solution",
    ph: 9,
    band: "weak-base",
    why: "Sodium bicarbonate hydrolyses partially: HCO₃⁻ + H₂O → H₂CO₃ + OH⁻ - giving a mildly basic solution.",
  },
  {
    id: "borax",
    name: "Borax solution",
    ph: 9.3,
    band: "weak-base",
    why: "Sodium tetraborate hydrolyses to release OH⁻ ions, giving a moderately basic pH around 9–10.",
  },
  {
    id: "milk-magnesia",
    name: "Milk of magnesia",
    ph: 10.5,
    band: "weak-base",
    why: "Mg(OH)₂ has low solubility, so the saturated suspension only reaches pH ~10.5 - strong enough to neutralise stomach acid without burning.",
  },
  {
    id: "toothpaste",
    name: "Toothpaste",
    ph: 9,
    band: "weak-base",
    why: "Mildly basic so it can neutralise acids produced by oral bacteria - typical pH 8–9.",
  },
  // Strong bases
  {
    id: "ammonia-cleaner",
    name: "Household ammonia",
    ph: 11.5,
    band: "strong-base",
    why: "Concentrated NH₃ in water produces enough OH⁻ to push pH above 11 - handle with gloves and ventilation.",
  },
  {
    id: "bleach",
    name: "Bleach (NaOCl)",
    ph: 12.5,
    band: "strong-base",
    why: "Sodium hypochlorite solutions are strongly basic (pH 11–13). Never mix with acids - releases toxic Cl₂ gas.",
  },
  {
    id: "lye",
    name: "Lye (NaOH solution)",
    ph: 13.5,
    band: "strong-base",
    why: "Sodium hydroxide is a textbook strong base - fully dissociates to give very high [OH⁻] and pH > 13. Caustic to skin.",
  },
  {
    id: "koh",
    name: "Potassium hydroxide",
    ph: 14,
    band: "strong-base",
    why: "KOH dissociates completely in water, giving pH near 14 even at modest concentrations.",
  },
  {
    id: "drain-cleaner",
    name: "Drain cleaner",
    ph: 13,
    band: "strong-base",
    why: "Most drain cleaners are concentrated NaOH or KOH - pH 13–14, capable of saponifying fats and dissolving hair.",
  },
  {
    id: "limewater",
    name: "Limewater (Ca(OH)₂)",
    ph: 12.4,
    band: "strong-base",
    why: "Saturated calcium hydroxide reaches pH ~12.4 - limited by Ca(OH)₂'s low solubility but still strongly basic.",
  },
];

export const PH_BY_BAND: Record<PhBand, PhSubstance[]> = phSubstances.reduce(
  (acc, s) => {
    acc[s.band].push(s);
    return acc;
  },
  {
    "strong-acid": [],
    "weak-acid": [],
    neutral: [],
    "weak-base": [],
    "strong-base": [],
  } as Record<PhBand, PhSubstance[]>,
);